import { useMemo, useState } from 'react';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';

import ContextMenu from '@/components/ContextMenu/ContextMenu';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import type { TransactionRow } from '@/store/expenses-store/transactions/transactions.dto.ts';

import { resolveIcon } from '../helpers/iconRegistry';
import { amountStyle, formatSignedAmount } from '../TransactionsPage/utils/transactionColors';
import {
  transactionStatusLabel,
  transactionStatusTone,
  transactionStatusVariant,
} from '../TransactionsPage/utils/transactionStatus';
import { recipientName } from '../TransactionsPage/utils/transformExpense';

import tableStyles from './ExpensesTable.module.scss';
import ExpensesTable, { type Column } from './ExpensesTable.tsx';

type TransactionsTableProps = {
  data: TransactionRow[];
  onRowClick?: (row: TransactionRow) => void;
  autoFit?: boolean;
  onEdit?: (row: TransactionRow) => void;
  onDelete?: (row: TransactionRow) => void;
};

export const TransactionsTable = ({
  data,
  onRowClick,
  autoFit,
  onEdit,
  onDelete,
}: TransactionsTableProps) => {
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const closeMenu = () => {
    setMenuRowId(null);
    setMenuAnchorEl(null);
  };

  const menuRow = data.find((row) => row.id === menuRowId) ?? null;
  const hasRowActions = Boolean(onEdit || onDelete);

  const columns: Column<TransactionRow>[] = useMemo(
    () => [
      {
        key: 'date',
        label: 'Date',
        sortable: true,
        width: '130px',
        sortValue: (row) => {
          const t = new Date(row.createdAt ?? '').getTime();
          return Number.isNaN(t) ? 0 : t;
        },
      },
      {
        key: 'description',
        label: 'Description',
        sortable: true,
        sortValue: (row) => row.description ?? '',
        render: (value) =>
          value ? (
            <span className={tableStyles.descriptionCell} title={String(value)}>
              {String(value)}
            </span>
          ) : (
            '—'
          ),
      },
      {
        key: 'recipient',
        label: 'Recipient',
        sortable: true,
        sortValue: (row) => recipientName(row),
        render: (_, row) => {
          const name = recipientName(row);
          return name ? (
            <span className={tableStyles.descriptionCell} title={name}>
              {name}
            </span>
          ) : (
            '—'
          );
        },
      },
      {
        key: 'type',
        label: 'Category',
        width: '220px',
        sortable: true,
        render: (value, row) => {
          const Icon = resolveIcon(row.categoryIcon);
          return (
            <span className={tableStyles.categoryCell}>
              <span className={tableStyles.typeIcon} style={{ backgroundColor: row.categoryColor }}>
                <Icon className={tableStyles.typeIconSvg} aria-hidden />
              </span>
              {String(value)}
            </span>
          );
        },
      },
      {
        key: 'amount',
        label: 'Amount',
        sortable: true,
        width: '220px',
        render: (value, row) => (
          <span style={amountStyle(row.transactionType)}>
            {formatSignedAmount(value as number, row.transactionType)}
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: '130px',
        render: (value) => (
          <StatusPill
            label={transactionStatusLabel(value as string)}
            tone={transactionStatusTone(value as string)}
            variant={transactionStatusVariant(value as string)}
          />
        ),
      },
      {
        key: 'actions',
        label: '',
        width: '56px',
        render: (_value, row) => (
          <>
            {hasRowActions && row.method === 'manual' && (
              <button
                type="button"
                className={tableStyles.overflow}
                aria-label="Row actions"
                aria-haspopup="menu"
                aria-expanded={menuRowId === row.id}
                onClick={(event) => {
                  event.stopPropagation();
                  setMenuRowId(row.id);
                  setMenuAnchorEl(event.currentTarget);
                }}
              >
                <BiDotsVerticalRounded />
              </button>
            )}
          </>
        ),
      },
    ],
    [menuRowId, hasRowActions],
  );

  return (
    <>
      <ExpensesTable
        data={data}
        columns={columns}
        onRowClick={onRowClick}
        autoFit={autoFit}
        rowClassName={(row) => {
          if (row.status === 'pending') return tableStyles.pendingRow;
          if (row.status === 'cancelled') return tableStyles.cancelledRow;
          return undefined;
        }}
      />

      <ContextMenu
        isOpen={menuRowId !== null}
        onClose={closeMenu}
        anchorEl={menuAnchorEl}
        items={
          menuRow
            ? [
                ...(onEdit
                  ? [
                      {
                        label: 'Edit',
                        icon: <FiEdit2 size={14} />,
                        onClick: () => onEdit(menuRow),
                      },
                    ]
                  : []),
                ...(onDelete
                  ? [
                      {
                        label: 'Delete',
                        icon: <FiTrash2 size={14} />,
                        variant: 'danger' as const,
                        onClick: () => onDelete(menuRow),
                      },
                    ]
                  : []),
              ]
            : []
        }
      />
    </>
  );
};

export default TransactionsTable;
